import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ConfigProvider } from "antd";
import enUS from "antd/locale/en_US";
import deDE from "antd/locale/de_DE";
import { AuthProvider } from "./auth/AuthProvider";
import { CookieBanner } from "./components/common/CookieBanner";
import { Footer } from "./components/common/Footer";
import { usePageViewTracking } from "./hooks/usePageViewTracking";
import { ProtectedRoute } from "./components/common/ProtectedRoute";
const CookiesPage = lazy(() => import("./pages/CookiesPage").then(m => ({ default: m.CookiesPage })));
const ImprintPage = lazy(() => import("./pages/ImprintPage").then(m => ({ default: m.ImprintPage })));
const LiabilityPage = lazy(() => import("./pages/LiabilityPage").then(m => ({ default: m.LiabilityPage })));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage").then(m => ({ default: m.PrivacyPage })));
import { HomePage } from "./pages/HomePage";
import { HelpPage } from "./pages/HelpPage";
import { AboutPage } from "./pages/AboutPage";
import { UsWestCoastPage } from "./pages/Landing/UsWestCoastPage";
import { UsEastCoastPage } from "./pages/Landing/UsEastCoastPage";
const NewsletterConfirmedPage = lazy(() => import("./pages/NewsletterConfirmedPage").then(m => ({ default: m.NewsletterConfirmedPage })));
const NewsletterUnsubscribedPage = lazy(() => import("./pages/NewsletterUnsubscribedPage").then(m => ({ default: m.NewsletterUnsubscribedPage })));
const NewsletterErrorPage = lazy(() => import("./pages/NewsletterErrorPage").then(m => ({ default: m.NewsletterErrorPage })));
import { LocaleLayout } from "./i18n/LocaleLayout";
import { LocaleRedirect } from "./i18n/LocaleRedirect";
import { LocaleHead } from "./i18n/LocaleHead";
import { PUBLIC_ROUTE_PATTERNS } from "./i18n/localePath";

const TripLongPage = lazy(() => import("./components/longpage/TripLongPage").then(m => ({ default: m.TripLongPage })));
const LoginPage = lazy(() => import("./pages/LoginPage").then(m => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import("./pages/RegisterPage").then(m => ({ default: m.RegisterPage })));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage").then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage").then(m => ({ default: m.ResetPasswordPage })));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage").then(m => ({ default: m.VerifyEmailPage })));
const InviteLandingPage = lazy(() => import("./pages/InviteLandingPage").then(m => ({ default: m.InviteLandingPage })));
const PublicTripPage = lazy(() => import("./pages/PublicTripPage").then(m => ({ default: m.PublicTripPage })));
const SettingsPage = lazy(() => import("./pages/SettingsPage").then(m => ({ default: m.SettingsPage })));
const TripPrintPage = lazy(() => import("./pages/TripPrint/TripPrintPage").then(m => ({ default: m.TripPrintPage })));
const AdminLayout = lazy(() => import("./pages/Admin").then(m => ({ default: m.AdminLayout })));
const AdminAnonConfig = lazy(() => import("./pages/Admin").then(m => ({ default: m.AdminAnonConfig })));
const AdminDashboard = lazy(() => import("./pages/Admin").then(m => ({ default: m.AdminDashboard })));
const AdminDemoAuthors = lazy(() => import("./pages/Admin").then(m => ({ default: m.AdminDemoAuthors })));
const AdminErrors = lazy(() => import("./pages/Admin").then(m => ({ default: m.AdminErrors })));
const AdminLandingPages = lazy(() => import("./pages/Admin").then(m => ({ default: m.AdminLandingPages })));
const AdminModels = lazy(() => import("./pages/Admin").then(m => ({ default: m.AdminModels })));
const AdminPromoPhoto = lazy(() => import("./pages/Admin").then(m => ({ default: m.AdminPromoPhoto })));
const AdminPromoTrips = lazy(() => import("./pages/Admin").then(m => ({ default: m.AdminPromoTrips })));
const AdminTrips = lazy(() => import("./pages/Admin").then(m => ({ default: m.AdminTrips })));
const AdminUsage = lazy(() => import("./pages/Admin").then(m => ({ default: m.AdminUsage })));
const AdminUsers = lazy(() => import("./pages/Admin").then(m => ({ default: m.AdminUsers })));
const AdminViator = lazy(() => import("./pages/Admin").then(m => ({ default: m.AdminViator })));

export const ANT_LOCALES: Record<string, typeof enUS> = { en: enUS, de: deDE };

function RedirectToTrip({ hash }: { hash?: string }) {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/trips/${id}${hash ? `#${hash}` : ""}`} replace />;
}

/**
 * Trip pages render the Footer inside their own scroll container so it
 * scrolls past the last section instead of sitting outside the constrained
 * viewport. Skip the app-level Footer on those routes to avoid duplication.
 */
function PageViewTracker() {
  usePageViewTracking();
  return null;
}

export function GlobalCookieBanner() {
  return <CookieBanner />;
}

export function GlobalFooter() {
  const { pathname } = useLocation();
  const hostsOwnFooter =
    /^\/trips\/[^/]+$/.test(pathname) || pathname.startsWith("/share/");
  if (hostsOwnFooter) return null;
  return <Footer />;
}

function HtmlLangSync() {
  const { i18n } = useTranslation();
  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);
  return null;
}

export function AppRoutes() {
  return (
    <>
      <HtmlLangSync />
      <PageViewTracker />
      <GlobalCookieBanner />
      <Suspense>
        <Routes>
          {/* Locale-prefixed public/marketing routes */}
          <Route path="/:lang" element={<><LocaleLayout /><LocaleHead /></>}>
            <Route index element={<HomePage />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="help" element={<HelpPage />} />
            <Route path="legal/cookies" element={<CookiesPage />} />
            <Route path="legal/imprint" element={<ImprintPage />} />
            <Route path="legal/liability" element={<LiabilityPage />} />
            <Route path="legal/privacy" element={<PrivacyPage />} />
            <Route path="newsletter/confirmed" element={<NewsletterConfirmedPage />} />
            <Route path="newsletter/unsubscribed" element={<NewsletterUnsubscribedPage />} />
            <Route path="newsletter/error" element={<NewsletterErrorPage />} />
            <Route path="r/us-west-coast" element={<UsWestCoastPage />} />
            <Route path="r/us-east-coast" element={<UsEastCoastPage />} />
          </Route>

          {/* Auth routes — unprefixed */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/invitations/landing" element={<InviteLandingPage />} />
          <Route path="/share/:token" element={<PublicTripPage />} />

          {/* App routes — unprefixed */}
          <Route path="/trips/:id/print" element={<TripPrintPage />} />
          <Route
            path="/settings"
            element={
              <ProtectedRoute rejectAnonymous>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trips/:id"
            element={
              <ProtectedRoute>
                <TripLongPage />
              </ProtectedRoute>
            }
          />
          <Route path="/trips/:id/plan" element={<ProtectedRoute><RedirectToTrip /></ProtectedRoute>} />
          <Route path="/trips/:id/cost" element={<ProtectedRoute><RedirectToTrip hash="cost" /></ProtectedRoute>} />
          <Route path="/trips/:id/collab" element={<ProtectedRoute><RedirectToTrip hash="collab" /></ProtectedRoute>} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="promo" element={<AdminPromoTrips />} />
            <Route path="promo/:tokenId/photo" element={<AdminPromoPhoto />} />
            <Route path="trips" element={<AdminTrips />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="anon" element={<AdminAnonConfig />} />
            <Route path="demo-authors" element={<AdminDemoAuthors />} />
            <Route path="landing-pages" element={<AdminLandingPages />} />
            <Route path="models" element={<AdminModels />} />
            <Route path="viator" element={<AdminViator />} />
            <Route path="usage" element={<AdminUsage />} />
            <Route path="errors" element={<AdminErrors />} />
          </Route>

          {/* Unprefixed public routes → redirect to locale-prefixed */}
          {PUBLIC_ROUTE_PATTERNS.map((p) => (
            <Route key={p} path={p} element={<LocaleRedirect />} />
          ))}

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <GlobalFooter />
    </>
  );
}

export function App() {
  const { i18n } = useTranslation();
  const antLocale = ANT_LOCALES[i18n.language] ?? enUS;

  return (
    <ConfigProvider locale={antLocale}>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ConfigProvider>
  );
}
